<template>
  <div class="panel wallet-positions">
    <div class="header" :class="{ embedded }">
      <button v-if="!embedded" class="back" type="button" @click="$emit('close')">‹</button>
      <div>
        <strong>{{ embedded ? 'Positions' : walletName }}</strong>
        <span v-if="!embedded">{{ shortKey(wallet) }}</span>
      </div>
      <div class="tools">
        <div class="filter-picker">
          <IconAction name="filter" :label="`Filter: ${statusLabel}`" :active="status !== 'all'" :size="32" @click="filterOpen = !filterOpen" />
          <div v-if="filterOpen" class="filter-menu" role="menu">
            <button v-for="item in statusTabs" :key="item.key" type="button" :class="{ on: status === item.key }" @click="selectStatus(item.key)">{{ item.label }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="body">
      <div v-for="position in filteredPositions" :key="position.mint" role="button" :aria-label="position.open ? `Manage and close ${position.name} position` : `View ${position.name} position`" tabindex="0" @keydown.enter="openPosition(position)" @keydown.space.prevent="openPosition(position)" class="position-card" :class="{ open: position.open, unresolved: position.orderStatus }" @click="openPosition(position)">
        <div class="position-aside"><span class="coin-art">
          <img v-if="position.image && position.image !== 'pending'" :src="position.image" :alt="position.name" @error="position.image = ''">
          <span v-else>{{ coinGlyph(position) }}</span>
          <i :class="{ live: position.open }" aria-hidden="true"></i>
        </span><nav class="coin-links"><a v-for="link in coinLinks(position.mint)" :key="link.name" :href="link.url" target="_blank" rel="noopener noreferrer" @click.stop>{{ link.name }}</a></nav></div>
        <div class="position-main">
          <div class="position-head">
            <div><strong>{{ position.name }}</strong><span>{{ position.symbol }}</span><small class="duration">{{ duration(position) }}</small></div>
            <div class="position-status">
              <span v-if="position.auto" class="status-dot auto" title="AUTO" aria-label="AUTO"></span>
              <span class="status-dot" :class="position.orderStatus || !position.verified ? 'pending' : position.open ? 'active' : position.pnlLamports >= 0 ? 'positive' : 'negative'" :title="position.orderStatus ? position.orderStatus : !position.verified ? 'Awaiting confirmed cost basis' : position.open ? 'In position' : position.pnlLamports >= 0 ? 'Profitable' : 'Loss'" aria-hidden="true"></span>
              <em v-if="positionState(position)" :class="position.open ? 'open' : 'closed'">{{ positionState(position) }}</em>
            </div>
          </div>
          <div class="position-money">
            <span>{{ !position.verified ? 'Awaiting confirmed entry' : position.orderStatus ? 'Unconfirmed order' : position.open ? 'Value' : 'Received' }} <b v-if="position.verified && !position.orderStatus && !position.incompleteCashFlow">{{ solText(position.valueLamports) }}</b><b v-else>—</b></span>
            <strong v-if="position.incompleteCashFlow" class="unverified-cash-flow">Cash flow unknown</strong><strong v-else-if="position.verified && !position.orderStatus" :class="{ pos: position.pnlLamports > 0, neg: position.pnlLamports < 0 }">{{ pnlText(position.pnlLamports, position.spentLamports) }}</strong><strong v-else>Cost basis not confirmed</strong>
          </div>
          <div class="position-stats" :class="{ closed: !position.open }"><div v-if="position.open" class="position-metrics">
            <span><small>Holders</small><b>{{ metric(position.holders) }}</b></span>
            <span><small>Trades</small><b>{{ metric(position.marketTrades) }}</b></span>
            <span><small>Liquidity</small><b>{{ solCompact(position.liquidityLamports) }}</b></span>
            <span><small>Tx</small><b>{{ position.trades.length }}</b></span>
          </div>
          <div class="position-exits"><div v-for="(exit, i) in position.exits" :key="tradeKey(exit)" class="exit-row"><small :title="tradeReason(exit)">{{ tradeReason(exit) }}</small><b><span class="exit-label">SELL {{ i + 1 }}:</span><span class="exit-value" :class="{ pos: exit.pnlLamports > 0, neg: exit.pnlLamports < 0 }">{{ solText(exit.valueLamports) }} {{ position.verified ? pnlPercentText(exit.pnlLamports, exit.pnlBasisLamports) : "" }}</span></b></div><small v-if="!position.exits.length" class="no-exits">{{ position.open ? 'Waiting for exits' : 'No confirmed exits' }}</small></div></div>
        </div>
        <div class="position-foot">
          <span class="provenance">{{ provenance(position) }}</span>
          <span>{{ tradeTime(position.latestTrade) }}</span>
          <span class="mint">{{ shortKey(position.mint) }}</span>
        </div>
      </div>
      <div v-if="!loading && filteredPositions.length === 0" class="empty">No positions</div>
      <div v-if="loading" class="empty">Loading...</div>
      <div v-if="error" class="error">{{ error }}</div>
    </div>

    <div class="footer">
      <button type="button" class="pager-btn" :disabled="page <= 1 || loading" @click="prev">‹</button>
      <span class="pager-info">{{ page }}</span>
      <button type="button" class="pager-btn" :disabled="loading || trades.length < 64" @click="next">›</button>
    </div>

    <div v-if="currentPosition" class="modal-backdrop" @click.self="selectedPosition = null">
      <section class="detail" role="dialog" aria-modal="true" :aria-label="`${currentPosition.name} position trades`">
        <div class="detail-head">
          <div>
            <span class="coin-art small"><img v-if="currentPosition.image && currentPosition.image !== 'pending'" :src="currentPosition.image" :alt="currentPosition.name"><span v-else>{{ coinGlyph(currentPosition) }}</span></span>
            <span><b class="detail-coin">{{ currentPosition.name }}<em>{{ currentPosition.symbol }}</em></b><strong :class="{ pos: currentPosition.pnlLamports > 0, neg: currentPosition.pnlLamports < 0 }">{{ pnlText(currentPosition.pnlLamports, currentPosition.spentLamports) }}</strong><small>{{ duration(currentPosition) }} · {{ currentPosition.trades.length }} trades</small></span>
          </div>
          <button type="button" aria-label="Close position" @click="selectedPosition = null">×</button>
        </div>
        <div class="trade-list">
          <button v-if="currentPosition.open" type="button" class="trade-card proposed" :disabled="selling || !currentPosition.verified || currentPosition.pendingEntry || currentPosition.pendingExit || submittedMint === currentPosition.mint" @click="prepareExit(currentPosition)">
            <div class="top">
              <div class="side">
                <div class="trade-title"><strong class="pending-sell">SELL</strong><small>{{ currentPosition.pendingExit || submittedMint === currentPosition.mint ? 'SELL pending confirmation' : currentPosition.pendingEntry ? 'BUY pending confirmation' : !currentPosition.verified ? 'Balance not yet verified' : selling ? 'Submitting…' : 'Close entire position · 100%' }}</small></div>
                <small class="trade-reason">{{ currentPosition.pendingExit || submittedMint === currentPosition.mint ? "A SELL is already pending. Duplicate exit is blocked." : "Estimated exit at current market price; execution may differ." }}</small>
              </div>
              <div class="money"><span>≈ {{ solText(currentPosition.valueLamports) }}</span><span class="pnl" :class="{ pos: currentPosition.pnlLamports > 0, neg: currentPosition.pnlLamports < 0 }">{{ pnlText(currentPosition.pnlLamports, currentPosition.spentLamports) }}</span></div>
            </div>
          </button>

          <article v-for="t in currentPosition.trades" :key="tradeKey(t)" class="trade-card" :class="t.method">
            <div class="top">
              <div class="side">
                <div class="trade-title"><strong :class="t.method">{{ t.method }}</strong><time>{{ tradeTime(t) }}</time></div>
                <small v-if="tradeProvenance(t)" class="trade-provenance">{{ tradeProvenance(t) }}</small>
                <small class="trade-reason">{{ tradeReason(t) }}</small>
                </div>
              <div class="money">
                <span>{{ solText(t.valueLamports) }}</span>
                <span v-if="t.pnlLamports != null" class="pnl" :class="{ pos: t.pnlLamports > 0, neg: t.pnlLamports < 0 }">{{ pnlText(t.pnlLamports, t.pnlBasisLamports) }}</span>
              </div>
            </div>
            <div class="trade-values"><div class="pool"><span>cul <b>{{ poolText(t.cul) }}</b></span><span>cup <b>{{ poolText(t.cup) }}</b></span></div><b class="trade-amount">{{ formatAmountHuman(t.amount) }}</b></div>
            <div class="trade-bottom"><span>{{ shortKey(t.mint) }}</span><a :href="solscanTx(t.signature)" target="_blank" rel="noreferrer">{{ shortKey(t.signature) }}</a></div>
          </article>
        </div>
        <div v-if="exitDialog" class="exit-overlay">
          <section class="exit-dialog" role="alertdialog" aria-modal="true" aria-label="Close position">
            <strong>{{ exitState === 'error' ? 'Sell failed' : exitState === 'submitted' ? 'Sell submitted' : exitState === 'pending' ? 'Submitting sell…' : 'Close position?' }}</strong>
            <p v-if="exitState === 'confirm'">Sell all remaining tokens? Estimated proceeds: {{ solText(currentPosition.valueLamports) }} SOL.</p>
            <p v-else-if="exitState === 'pending'">Submitting the transaction. Do not submit again.</p>
            <p v-else-if="exitState === 'submitted'" :class="{ pos: currentPosition.pnlLamports > 0, neg: currentPosition.pnlLamports < 0 }">Request accepted · estimated {{ pnlText(currentPosition.pnlLamports, currentPosition.spentLamports) }}. Awaiting confirmation.</p>
            <p v-else>{{ sellError }}</p>
            <div class="exit-actions"><button v-if="exitState !== 'pending'" type="button" @click="exitDialog = false">{{ exitState === 'confirm' ? 'Cancel' : 'Close' }}</button><button v-if="exitState === 'confirm'" type="button" class="confirm" @click="closePosition(currentPosition)">Confirm SELL</button></div>
          </section>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStorage } from '@/stores'
import { formatAmountHuman, formatSol, shortKey } from '@/composables/format'
import { formatPnlWithPct } from '@/components/_shared/format'
import IconAction from '@/components/_shared/IconAction.vue'

const props = defineProps({
  wallet: { type: String, required: true },
  embedded: { type: Boolean, default: false },
})

defineOptions({ name: 'WalletPositions' })
defineEmits(['close'])

const store = useStorage()
const page = ref(1)
const trades = ref([])
const status = ref('all')
const filterOpen = ref(false)
const loading = ref(false)
const error = ref('')
const selectedPosition = ref(null)
const selling = ref(false)
const submittedMint = ref('')
const sellError = ref('')
const exitDialog = ref(false)
const exitState = ref('confirm')
const now = ref(Date.now())
const currentPosition = computed(() => positions.value.find(p => p.mint === selectedPosition.value) || null)
const statusTabs = [{ key: 'all', label: 'All' }, { key: 'open', label: 'Open' }, { key: 'closed', label: 'Closed' }]
const statusLabel = computed(() => statusTabs.find(item => item.key === status.value)?.label || 'All')
function selectStatus(value) { status.value = statusTabs.some(item => item.key === value) ? value : 'all'; filterOpen.value = false }

const walletName = computed(() => {
  const w = Object.values(store.wallets || {}).find(x => (x.public_key || x.publicKey) === props.wallet)
  return w?.name || 'Wallet positions'
})

const enrichedTrades = computed(() => {
  const rows = Array.isArray(trades.value) ? trades.value : []
  const state = new Map()
  const out = []
  for (const trade of rows.slice().reverse()) {
    const mint = String(trade?.mint || '').trim()
    const method = String(trade?.method || '').trim()
    const amount = num(trade?.amount)
    const value = Math.abs(signedLamports(trade) || num(trade?.lamports))
    const pos = state.get(mint) || { tokens: 0, cost: 0 }
    let pnl = null
    let pnlBasis = 0
    if ((method === 'buy' || method === 'add') && amount > 0) {
      pos.tokens += amount
      pos.cost += value
    } else if (method === 'sell' && amount > 0 && pos.tokens > 0 && pos.cost > 0) {
      const sold = Math.min(amount, pos.tokens)
      const cost = pos.cost * sold / pos.tokens
      pnlBasis = cost
      pnl = Math.round(value - cost)
      pos.tokens = Math.max(0, pos.tokens - sold)
      pos.cost = Math.max(0, pos.cost - cost)
    }
    state.set(mint, pos)
    out.push({ ...trade, raw: trade, method, valueLamports: value, pnlLamports: pnl, pnlBasisLamports: pnlBasis })
  }
  return out.reverse()
})

const walletLifecycles = computed(() => {
  const root = store.state?.value || store.state || {}
  const rows = root.positionLifecycles || []
  return (Array.isArray(rows) ? rows : []).filter(row => String(row?.wallet || row?.owner || '') === props.wallet)
})

const positions = computed(() => {
  const groups = new Map()
  for (const trade of enrichedTrades.value.slice().reverse()) {
    const mint = String(trade?.mint || '').trim()
    if (!mint) continue
    const amount = num(trade?.amount)
    const value = num(trade?.valueLamports)
    const position = groups.get(mint) || { mint, tokens: 0, cost: 0, spentLamports: 0, receivedLamports: 0, realizedLamports: 0, trades: [], latestTrade: trade, entryTrade: null }
    if (trade.method === 'buy' || trade.method === 'add') {
      position.tokens += amount
      position.cost += value
      position.spentLamports += value
      if (!position.entryTrade) position.entryTrade = trade
    } else if (trade.method === 'sell' && amount > 0) {
      const before = position.tokens
      const sold = Math.min(amount, before)
      const cost = before > 0 ? position.cost * sold / before : 0
      position.tokens = Math.max(0, before - sold)
      position.cost = Math.max(0, position.cost - cost)
      position.receivedLamports += value
      position.realizedLamports += value - cost
    }
    position.latestTrade = trade
    position.trades.unshift(trade)
    groups.set(mint, position)
  }
  return [...groups.values()].map(position => {
    const coin = marketCoin(position.mint)
    const amount = num(coin?.amount || coin?.poolAmountRaw || coin?.pool_amount_raw)
    const lamports = num(coin?.lamports || coin?.poolLamports || coin?.pool_lamports)
    const price = amount > 0 && lamports > 0 ? lamports / amount : 0
    const aggregateOpen = position.tokens > 0
    const aggregateUnrealized = aggregateOpen && price > 0 ? position.tokens * price - position.cost : 0
    const lifecycle = lifecycleFor(position)
    const fallbackName = String(position.entryTrade?.coin_name || position.entryTrade?.name || lifecycle?.coin?.name || '').trim()
    const fallbackSymbol = String(position.entryTrade?.coin_symbol || position.entryTrade?.symbol || lifecycle?.coin?.symbol || '').trim()
    const sourceTrade = lifecycleEntryTrade(lifecycle, position.entryTrade || position.latestTrade)
    const canonicalTrades = lifecycleTrades(lifecycle, position.trades)
    const pendingEntry = canonicalTrades.some(t => t.method === 'buy' && String(t.status || '').toLowerCase() === 'pending')
    const pendingExit = canonicalTrades.some(t => t.method === 'sell' && String(t.status || '').toLowerCase() === 'pending')
    const open = lifecycle ? lifecycleIsOpen(lifecycle) : false
    // An accepted, failed or unresolved intent is not a confirmed token position.
    const latestStatus = String(position.latestTrade?.status || '').toLowerCase()
    const hasPendingBuy = position.trades.some(t => t.method === 'buy' && ['pending', 'submitted', 'submitted_unresolved'].includes(String(t.status || '').toLowerCase()))
    // An early SELL receipt must never be counted as profit until the BUY
    // receipt establishes the actual on-chain cost, not the submitted quote.
    const orderStatus = !lifecycle
      ? hasPendingBuy ? 'pending' : ['pending', 'submitted', 'submitted_unresolved', 'failed', 'expired', 'replaced'].includes(latestStatus) ? latestStatus : 'verifying'
      : ''
    const incompleteCashFlow = Boolean(lifecycle?.incomplete_cash_flow ?? lifecycle?.incompleteCashFlow)
    const canonicalSpent = lifecycle ? num(lifecycle.spent_lamports ?? lifecycle.spentLamports) : position.spentLamports
    const canonicalReceived = lifecycle ? num(lifecycle.received_lamports ?? lifecycle.receivedLamports) : position.receivedLamports
    const canonicalPnl = lifecycle ? num(lifecycle.total_lamports ?? lifecycle.totalLamports) : Math.round(position.realizedLamports + aggregateUnrealized)
    return {
      ...position,
      open,
      orderStatus,
      incompleteCashFlow,
      verified: Boolean(lifecycle),
      pendingEntry,
      pendingExit,
      name: String(coin?.name || lifecycle?.coin?.name || fallbackName || fallbackSymbol || shortKey(position.mint)).trim(),
      symbol: String(coin?.symbol || lifecycle?.coin?.symbol || fallbackSymbol || 'TOKEN').trim(),
      image: String(coin?.image || lifecycle?.coin?.image || position.entryTrade?.image || '').trim(),
      holders: num(coin?.holders_count ?? coin?.holdersCount ?? coin?.holders),
      marketTrades: num(coin?.trades ?? coin?.trades_count ?? coin?.tradesCount),
      liquidityLamports: num(coin?.liquidity_lamports ?? coin?.liquidityLamports ?? coin?.poolLamports ?? coin?.lamports),
      spentLamports: canonicalSpent,
      receivedLamports: canonicalReceived,
      valueLamports: open ? Math.max(0, Math.round(canonicalSpent + canonicalPnl)) : Math.round(canonicalReceived),
      pnlLamports: Math.round(canonicalPnl),
      latestTrade: canonicalTrades[canonicalTrades.length - 1] || position.latestTrade,
      entryTrade: sourceTrade,
      trades: canonicalTrades,
      durationMs: lifecycle ? num(lifecycle.duration_ms ?? lifecycle.durationMs) : 0,
      auto: isAutomaticTrade(sourceTrade),
      sourceLabel: sourceLabel(sourceTrade),
      sourceClass: sourceClass(sourceTrade),
      exits: canonicalTrades.filter(t => t.method === 'sell' && t.pnlLamports != null).slice().reverse(),
    }
  }).sort((a, b) => tradeStamp(b.latestTrade) - tradeStamp(a.latestTrade))
})

const filteredPositions = computed(() => {
  if (status.value === 'all') return positions.value
  return positions.value.filter(position => !position.orderStatus && (status.value === 'open' ? position.open : !position.open))
})

onMounted(() => {
  load()
  document.addEventListener('pointerdown', onDocumentPointerDown)
})
onBeforeUnmount(() => { document.removeEventListener('pointerdown', onDocumentPointerDown) })
watch(() => props.wallet, () => { page.value = 1; selectedPosition.value = null; submittedMint.value = ''; load() })

// Trade history is fetched on entry and when canonical websocket lifecycle transitions,
// never through a periodic HTTP poll. Market values remain reactive from the live store.
const lifecycleRevision = computed(() => {
  const rows = store.state?.positionLifecycles || []
  return JSON.stringify((Array.isArray(rows) ? rows : [])
    .filter(row => String(row?.wallet || row?.owner || '') === props.wallet)
    .map(row => [
      row.mint,
      row.stage,
      row.entry_at || row.opened_at,
      row.closed_at,
      row.remaining_token_amount_raw ?? row.token_amount_raw,
      Array.isArray(row.steps) ? row.steps.length : 0,
    ]))
})
watch(lifecycleRevision, (next, previous) => {
  if (previous === undefined || next === previous) return
  submittedMint.value = ''
  now.value = Date.now()
  load(true)
})

let request = 0
async function load(silent = false) {
  if (!props.wallet) return
  const id = ++request
  const wallet = props.wallet
  if (!silent) loading.value = true
  error.value = ''
  try {
    const res = await store.walletTrades(wallet, page.value)
    if (id === request && wallet === props.wallet) trades.value = Array.isArray(res.trades) ? res.trades : []
  } catch (e) {
    if (id === request) error.value = e?.message || 'Wallet positions failed'
  } finally {
    if (id === request) loading.value = false
  }
}
function next() { page.value += 1; load() }
function prev() { if (page.value <= 1) return; page.value -= 1; load() }
function openPosition(position) { filterOpen.value = false; sellError.value = ''; exitDialog.value = false; selectedPosition.value = position.mint }
function prepareExit(position) {
  if (selling.value || submittedMint.value === position?.mint || !position?.open || !position.verified || position.pendingEntry || position.pendingExit) return
  exitState.value = 'confirm'
  sellError.value = ''
  exitDialog.value = true
}
async function closePosition(position) {
  if (selling.value || submittedMint.value === position?.mint || !position?.open || !position.verified || position.pendingEntry || position.pendingExit) return
  exitState.value = 'pending'
  selling.value = true
  sellError.value = ''
  try {
    await store.tradeWallet(position.mint, props.wallet, 'sell', 100)
    submittedMint.value = position.mint
    exitState.value = 'submitted'
    await load(true)
  } catch (error) {
    const msg = String(error?.message || 'Sell request failed')
    sellError.value = /token balance is zero/i.test(msg) ? 'No tokens remain. Position state needs reconciliation.' : 'Sell failed. Check current position status.'
    exitState.value = 'error'
  } finally {
    selling.value = false
  }
}
function onDocumentPointerDown(event) {
  if (!filterOpen.value) return
  const target = event?.target
  if (target instanceof Element && target.closest('.filter-picker')) return
  filterOpen.value = false
}
function marketCoin(mint) {
  const direct = store.coins?.[mint] || store.mints?.[mint]
  if (direct) return direct
  return [...Object.values(store.coins || {}), ...Object.values(store.mints || {})].find(item => String(item?.mint || '') === mint) || {}
}
function tradeKey(t) { return `${t?.id || t?.trade_id || ''}:${t?.signature || ''}:${t?.method || ''}:${t?.mint || ''}:${t?.created_at || ''}` }
function tradeStamp(trade) { const value = Date.parse(String(trade?.created_at || trade?.createdAt || '')); return Number.isFinite(value) ? value : 0 }
function solscanTx(signature) { return `https://solscan.io/tx/${encodeURIComponent(String(signature || ''))}` }
function solText(lamports) { return formatSol(Math.max(0, Number(lamports) || 0)) }
function solCompact(lamports) { return `${(Math.max(0, Number(lamports) || 0) / 1e9).toLocaleString(undefined, { maximumFractionDigits: 2 })} SOL` }
function pnlText(lamports, basisLamports) { return formatPnlWithPct(lamports, basisLamports) }
function poolText(value) { return value == null || String(value).trim() === '' ? 'unknown' : formatAmountHuman(value) }
function metric(value) { return new Intl.NumberFormat(undefined, { notation: Number(value) >= 1000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(Math.max(0, Number(value) || 0)) }
function coinGlyph(position) { return String(position?.symbol || position?.name || '?').trim().slice(0, 1).toUpperCase() || '?' }
function tradeTime(trade) {
  const raw = trade?.created_at || trade?.createdAt || trade?.timestamp || ''
  const date = new Date(raw)
  if (!raw || Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(date)
}
function sourceLabel(trade) {
  const source = String(trade?.source || '').trim()
  if (source === 'ui_plugin') return pluginLabel(trade)
  if (isAutomaticTrade(trade)) return strategyLabel(trade)
  return source === 'manual' ? 'Manual' : source ? source : ''
}
function sourceClass(trade) {
  const source = String(trade?.source || '').trim()
  if (source === 'ui_plugin') return 'plugin'
  if (isAutomaticTrade(trade)) return 'strategy'
  return ''
}
function isAutomaticTrade(trade) {
  const source = String(trade?.source || '').trim()
  return ['autotrade', 'strategy', 'server_autosnipe', 'lua'].includes(source)
    || num(trade?.strategy_id ?? trade?.strategyId) > 0
    || num(trade?.strategy_run_id ?? trade?.strategyRunId) > 0
}
function strategyLabel(trade) {
  const id = num(trade?.strategy_id ?? trade?.strategyId)
  const version = num(trade?.strategy_version ?? trade?.strategyVersion)
  return [id > 0 && `strategy ${id}`, version > 0 && `v${version}`].filter(Boolean).join(' · ')
}
function pluginLabel(trade) {
  const name = pluginName(trade)
  const semantic = String(trade?.ui_plugin_semantic_version || trade?.uiPluginSemanticVersion || '').trim()
  return semantic ? `${name} · v${semantic.replace(/^v/, '')}` : name
}

function coinLinks(mint) {
  const id = encodeURIComponent(mint)
  return [{name:'Pump.fun',url:`https://pump.fun/coin/${id}`},{name:'Solscan',url:`https://solscan.io/token/${id}`},{name:'GMGN',url:`https://gmgn.ai/sol/token/${id}`},{name:'Axiom',url:`https://axiom.trade/t/${id}`}]
}
function duration(position) {
  if (num(position?.durationMs) > 0) {
    const seconds = Math.floor(num(position.durationMs) / 1000)
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor(seconds % 3600 / 60)
    return hours ? `${hours}h ${minutes}m` : `${minutes}m ${seconds % 60}s`
  }
  const start = tradeStamp(position.entryTrade)
  if (!start) return '—'
  const seconds = Math.max(0, Math.floor(((position.open ? now.value : tradeStamp(position.latestTrade)) - start) / 1000))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor(seconds % 3600 / 60)
  return hours ? `${hours}h ${minutes}m` : `${minutes}m ${seconds % 60}s`
}
function readableReason(value) {
  const key = String(value || '').trim().replace(/^strategy_exit:/, '').toLowerCase()
  const labels = {
    take_profit: 'Take profit',
    take_1: 'Take 1',
    take1: 'Take 1',
    take_2: 'Take 2',
    take2: 'Take 2',
    take_3: 'Take 3',
    take3: 'Take 3',
    take_4: 'Take 4',
    take4: 'Take 4',
    trailing_stop: 'Protect profit',
    stop_loss: 'Loss limit',
    max_hold: 'Time limit reached',
    timeout: 'Time limit reached',
    early_momentum_cut: 'Momentum weakened',
    early_price_liquidity_cut: 'Price/liquidity drop',
    early_profit_protect: 'Early profit protection',
    early_post_entry_collapse: 'Entry momentum lost',
    manual_zero_balance: 'Balance reconciled',
    manual: 'Closed manually',
    manual_ui_intent: 'Manual entry',
  }
  if (labels[key]) return labels[key]
  if (!key) return ''
  const text = key.replace(/[_:-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return text ? text[0].toUpperCase() + text.slice(1) : ''
}
function tradeReason(trade) {
  const reason = trade?.method === 'sell'
    ? (trade?.exit_reason || trade?.exitReason || '')
    : (trade?.entry_reason || trade?.entryReason || '')
  const readable = readableReason(reason)
  if (readable) return readable
  if (trade?.method === 'buy') return isAutomaticTrade(trade) ? 'Automatic entry' : 'Manual entry'
  return isAutomaticTrade(trade) ? 'Automatic exit' : trade?.source === 'manual' ? 'Manual exit' : 'Exit reason unavailable'
}
function positionState(position) {
  if (position.incompleteCashFlow) return 'CASH FLOW UNKNOWN'
  if (position.orderStatus) return position.orderStatus === 'submitted_unresolved' ? 'UNRESOLVED BUY' : position.orderStatus === 'failed' ? 'BUY FAILED' : position.orderStatus === 'incomplete' ? 'COST BASIS UNKNOWN' : position.orderStatus.toUpperCase()
  if (position.pendingEntry) return 'ENTERING'
  if (position.pendingExit) return 'EXITING'
  return position.open ? 'IN POSITION' : ''
}
function pnlPercentText(value, basis) {
  const cost = Math.abs(num(basis))
  if (!cost) return ''
  const pct = num(value) * 100 / cost
  return `(${pct >= 0 ? '+' : ''}${pct.toFixed(Math.abs(pct) >= 100 ? 0 : 1)}%)`
}
function provenance(position) {
  const entry = position.entryTrade || {}
  const semver = String(entry.ui_plugin_semantic_version || entry.uiPluginSemanticVersion || '').trim()
  const channel = String(entry.ui_plugin_channel || entry.uiPluginChannel || '').trim()
  const build = num(entry.ui_plugin_build ?? entry.ui_plugin_version ?? entry.uiPluginVersion)
  const sha = String(entry.ui_plugin_source_sha || entry.uiPluginSourceSha || '').trim()
  if (semver || build > 0 || sha) {
    return [semver && (channel ? channel + ': ' : '') + 'v' + semver.replace(/^v/, ''), build > 0 && 'build ' + build, sha && 'deploy ' + sha.slice(0, 8)].filter(Boolean).join(' · ')
  }
  // Server AUTO has strategy provenance, not an Extension release. Keep
  // both kinds in the same card slot without assigning a current game version.
  if (isAutomaticTrade(entry)) {
    const origin = String(entry.source || '').trim()
    const strategy = strategyLabel(entry)
    return [origin === 'server_autosnipe' && 'Server AUTO', strategy].filter(Boolean).join(' · ') || 'AUTO · Version unavailable'
  }
  if (String(entry.source || '').trim() === 'manual') return 'Manual'
  return 'Provenance unavailable'
}
function tradeProvenance(trade, includeName = true) {
  if (String(trade?.source || '').trim() !== 'ui_plugin') return sourceLabel(trade)
  const name = pluginName(trade)
  const channel = String(trade?.ui_plugin_channel || trade?.uiPluginChannel || '').trim()
  const version = String(trade?.ui_plugin_semantic_version || trade?.uiPluginSemanticVersion || '').trim()
  const build = Number(trade?.ui_plugin_version || trade?.uiPluginVersion || 0)
  const deploy = String(trade?.ui_plugin_source_sha || trade?.uiPluginSourceSha || '').trim()
  return [includeName && name, channel && `${channel}: ${version ? `v${version.replace(/^v/, '')}` : 'unknown'}`, build > 0 && `build ${build}`, deploy && `deploy ${deploy.slice(0, 8)}`].filter(Boolean).join(' · ')
}
function lifecycleFor(position) {
  const entryID = num(position?.entryTrade?.id ?? position?.entryTrade?.trade_id)
  return walletLifecycles.value.find(row => entryID > 0 && num(row?.entry?.trade_id ?? row?.entry?.tradeId) === entryID)
    || walletLifecycles.value.find(row => String(row?.mint || '') === position.mint)
    || null
}
function lifecycleIsOpen(row) {
  const remaining = row?.remaining_token_amount_raw ?? row?.remainingTokenAmountRaw
  if (remaining != null && String(remaining).trim() !== '') return Number(remaining) > 0
  return String(row?.status || '').toLowerCase() === 'open'
}
function lifecycleEntryTrade(row, fallback) {
  if (!row?.entry) return fallback
  const entry = row.entry
  return {
    ...(fallback || {}),
    id: entry.trade_id ?? entry.tradeId ?? fallback?.id,
    mint: row.mint,
    method: 'buy',
    created_at: row.entry_at ?? row.entryAt ?? fallback?.created_at,
    amount: entry.token_amount_raw ?? entry.tokenAmountRaw ?? fallback?.amount,
    valueLamports: num(entry.value_lamports ?? entry.valueLamports ?? fallback?.valueLamports),
    source: entry.source || fallback?.source,
    source_id: entry.source_id ?? entry.sourceId ?? fallback?.source_id,
    entry_reason: entry.reason || fallback?.entry_reason,
    strategy_id: entry.strategy_id ?? entry.strategyId ?? fallback?.strategy_id,
    strategy_version: entry.strategy_version ?? entry.strategyVersion ?? fallback?.strategy_version,
    strategy_run_id: entry.strategy_run_id ?? entry.strategyRunId ?? fallback?.strategy_run_id,
    strategy_signal_id: entry.strategy_signal_id ?? entry.strategySignalId ?? fallback?.strategy_signal_id,
    ui_plugin_id: entry.plugin_id ?? entry.pluginId ?? fallback?.ui_plugin_id,
    ui_plugin_version: entry.plugin_version ?? entry.pluginVersion ?? fallback?.ui_plugin_version,
    signature: entry.signature || fallback?.signature,
  }
}
function lifecycleTrades(row, fallbackTrades) {
  if (!row?.entry) return fallbackTrades
  const fallback = fallbackTrades || []
  const existing = new Map(fallback.map(trade => [num(trade?.id ?? trade?.trade_id), trade]))
  const entry = lifecycleEntryTrade(row, existing.get(num(row.entry?.trade_id ?? row.entry?.tradeId)))
  let tokens = num(entry?.amount)
  let cost = num(entry?.valueLamports)
  const seen = new Set([num(entry?.id)])
  const timeline = (Array.isArray(row.timeline) ? row.timeline : []).map(exit => {
    const id = num(exit?.trade_id ?? exit?.tradeId)
    const current = existing.get(id) || {}
    const amount = num(exit?.token_amount_raw ?? exit?.tokenAmountRaw ?? current.amount)
    const value = num(exit?.value_lamports ?? exit?.valueLamports ?? current.valueLamports)
    const sold = tokens > 0 ? Math.min(amount, tokens) : amount
    const basis = tokens > 0 ? cost * sold / tokens : 0
    tokens = Math.max(0, tokens - sold)
    cost = Math.max(0, cost - basis)
    seen.add(id)
    return {
      ...current,
      id,
      mint: row.mint,
      method: 'sell',
      status: current.status || 'confirmed',
      created_at: exit.at || current.created_at,
      amount,
      valueLamports: value,
      source: exit.source || current.source,
      exit_reason: exit.reason || current.exit_reason,
      strategy_id: exit.strategy_id ?? exit.strategyId ?? current.strategy_id,
      strategy_version: exit.strategy_version ?? exit.strategyVersion ?? current.strategy_version,
      strategy_run_id: exit.strategy_run_id ?? exit.strategyRunId ?? current.strategy_run_id,
      strategy_signal_id: exit.strategy_signal_id ?? exit.strategySignalId ?? current.strategy_signal_id,
      signature: exit.signature || current.signature,
      pnlLamports: Math.round(value - basis),
      pnlBasisLamports: Math.round(basis),
    }
  })
  const pending = fallback.filter(trade => String(trade?.status || '').toLowerCase() === 'pending' && !seen.has(num(trade?.id ?? trade?.trade_id)))
  return [entry, ...timeline, ...pending].filter(Boolean).sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
}
function pluginName(trade) {
  const pluginId = String(trade?.ui_plugin_id || trade?.uiPluginId || '').trim()
  const sourceId = String(trade?.source_id || trade?.sourceId || '').trim()
  const installation = (store.uiPluginInstallations || []).find(item => {
    const id = String(item?.id || '').trim()
    const resolvedPluginId = String(item?.plugin_id || item?.pluginId || item?.plugin?.id || '').trim()
    return (pluginId && resolvedPluginId === pluginId) || (sourceId && id === sourceId)
  })
  return String(installation?.name || installation?.plugin?.name || installation?.slug || installation?.plugin?.slug || 'Plugin').trim()
}
function num(value) { const n = Number(value || 0); return Number.isFinite(n) ? n : 0 }
function signedLamports(trade) {
  const raw = trade?.net_lamports ?? trade?.netLamports
  const net = Number(raw || 0)
  if (Number.isFinite(net) && net !== 0) return net
  const lamports = num(trade?.lamports)
  return trade?.method === 'sell' ? lamports : -lamports
}
</script>

<style scoped>
.panel{position:relative;background:#111;border:1px solid #222;border-radius:8px;overflow:hidden;height:100%;display:flex;flex-direction:column}
.header{padding:10px;border-bottom:1px solid #222;background:#1a1a1a;display:grid;grid-template-columns:30px minmax(0,1fr) auto;gap:8px;align-items:center}.header.embedded{grid-template-columns:minmax(0,1fr) auto;min-height:48px}.header.embedded :deep(.icon-action-button svg){width:68%;height:68%;stroke-width:2.2}.header strong,.header span{display:block;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.header strong{color:#fff;font-size:14px}.header span{color:#8fa1b5;font-size:11px}
.back,.tools button,.footer button,.detail-head button{height:28px;border:1px solid #333;border-radius:999px;background:#111;color:#aaa;cursor:pointer}.tools{display:flex;gap:5px}.filter-picker{position:relative}.filter-menu{position:absolute;z-index:20;right:0;top:34px;min-width:112px;padding:5px;border:1px solid #2b3540;border-radius:9px;background:#090d12f5;box-shadow:0 14px 40px #000c}.filter-menu button{display:block;width:100%;text-align:left;border:0;border-radius:6px;background:transparent}.filter-menu button.on{background:#10213a;color:#dbeafe}.tools button{padding:0 8px;font-size:11px}
.body{flex:1 1 auto;min-height:0;overflow-y:auto;padding:8px;display:flex;flex-direction:column;gap:8px}
.position-aside{min-width:0;display:grid;align-content:start;gap:8px}.coin-links{display:grid;gap:4px}.coin-links a{font-size:9px;line-height:1.25;text-decoration:none;color:#7f8c9b;white-space:nowrap}.coin-links a:hover{color:#7dd3fc}.duration{font-size:9px;color:#64748b;font-weight:500}.position-stats{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:6px;align-content:start}.position-stats.closed{grid-template-columns:minmax(0,1fr)}.position-stats.closed .position-exits{max-height:none}.position-exits{display:grid;align-content:start;gap:4px;max-height:80px;min-width:0;overflow-y:auto}.exit-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.35fr);align-items:start;gap:8px;min-width:0}.exit-row small{display:block;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:8px;color:#f8fafc}.no-exits{font-size:8px;color:#64748b}.exit-row b{display:grid;grid-template-columns:auto minmax(0,1fr);align-items:baseline;gap:6px;min-width:0;font-size:9px;font-variant-numeric:tabular-nums}.exit-row .exit-label{white-space:nowrap;color:#94a3b8;font-weight:500}.exit-row .exit-value{text-align:right;white-space:nowrap;min-width:0;overflow:hidden;text-overflow:ellipsis}.exit-row .exit-value.pos{color:#22c55e}.exit-row .exit-value.neg{color:#f87171}.provenance{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.position-card{position:relative;width:100%;display:grid;grid-template-columns:54px minmax(0,1fr);gap:10px;padding:10px;text-align:left;border:1px solid #242b32;border-radius:10px;background:linear-gradient(145deg,#0c1014,#0d0d0d);color:#e5e7eb;cursor:pointer}.position-card.open{border-color:#214254;box-shadow:inset 0 0 24px #08202b66}.position-card:hover{border-color:#386078}
.coin-art{position:relative;width:54px;height:54px;display:grid;place-items:center;overflow:hidden;border:1px solid #263541;border-radius:10px;background:radial-gradient(circle at 50% 40%,#153b50,#07121a 68%);color:#9ee8ff;font-size:20px;font-weight:900}.coin-art img{width:100%;height:100%;object-fit:cover}.coin-art i{position:absolute;right:4px;bottom:4px;width:7px;height:7px;border-radius:50%;background:#64748b;border:1px solid #071015}.coin-art i.live{background:#22c55e;box-shadow:0 0 8px #22c55e}.coin-art.small{width:36px;height:36px;border-radius:8px;font-size:14px}
.position-main{min-width:0;display:grid;align-content:start;gap:7px}.position-head{padding-right:92px;min-width:0;width:100%;overflow:hidden}.position-head,.position-money,.position-foot{display:flex;align-items:center;justify-content:space-between;gap:8px}.position-head>div{min-width:0;max-width:100%;display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;overflow:hidden}.position-head strong{min-width:0;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#fff;font-size:13px}.position-head span{color:#7dd3fc;font-size:10px;font-weight:800}.position-status{position:absolute;top:12px;right:12px;display:flex;align-items:center;gap:5px;min-width:0}.status-dot{width:7px;height:7px;flex:0 0 auto;border-radius:999px;background:#64748b}.status-dot.auto{background:#a855f7;box-shadow:0 0 8px #a855f788}.status-dot.active{background:#eab308;box-shadow:0 0 8px #eab30888}.status-dot.positive{background:#22c55e;box-shadow:0 0 8px #22c55e66}.status-dot.negative{background:#ef4444;box-shadow:0 0 8px #ef444466}.position-head em{max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#94a3b8;font-size:9px;font-style:normal;font-weight:700}.position-head em.open{border-color:#166534;color:#facc15}.position-head em.closed{color:#94a3b8}
.position-money{min-width:0;overflow:hidden;font-size:11px;color:#7f8c9b}.position-money>span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.position-money>strong{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:right}.position-money b{color:#f8fafc;font-size:13px}.position-money>strong{font-size:12px;font-variant-numeric:tabular-nums}
.position-metrics{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}.position-metrics span{min-width:0;padding:5px 6px;border:1px solid #202b33;border-radius:6px;background:#091016;display:grid;gap:2px}.position-metrics small{overflow:hidden;text-overflow:ellipsis;color:#64748b;font-size:8px;text-transform:uppercase}.position-metrics b{overflow:hidden;text-overflow:ellipsis;color:#cbd5e1;font-size:10px;white-space:nowrap;text-align:center}.position-foot{grid-column:1/-1;min-width:0;justify-content:flex-start;color:#64748b;font-size:9px;white-space:nowrap}.position-foot .mint{margin-left:auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.source-pill{width:max-content;border:1px solid #334155;border-radius:999px;padding:2px 6px;color:#cbd5e1;background:#151515;font-size:9px;font-style:normal;font-weight:900;line-height:1;text-transform:uppercase}.source-pill.robot{border-color:#78350f;color:#facc15;background:#1c1608}.source-pill.strategy{border-color:#1d4ed8;color:#93c5fd;background:#0c1526}.source-pill.plugin{border-color:#047857;color:#86efac;background:#071a13;text-transform:none}
.pos{color:#22c55e}.neg{color:#f87171}.empty,.error{padding:14px;text-align:center;color:#64748b}.error{color:#fca5a5}.footer{min-height:38px;border-top:1px solid #222;padding:6px 8px;display:flex;align-items:center;justify-content:flex-end;gap:6px;color:#8fa1b5;font-size:12px}.pager-btn{width:28px;padding:0;font-size:16px;line-height:1}.pager-info{min-width:28px;height:24px;display:inline-flex;align-items:center;justify-content:center;border:1px solid #2a2a2a;border-radius:999px;background:#101010;color:#cbd5e1;font-size:11px;font-weight:800}
.modal-backdrop{position:absolute;inset:0 0 calc(var(--app-bottom-nav-space,58px) + env(safe-area-inset-bottom)) 0;z-index:20;display:flex;align-items:center;justify-content:center;padding:12px;background:rgba(0,0,0,.76)}.detail{position:relative;width:min(620px,100%);max-height:100%;min-height:0;display:flex;flex-direction:column;border:1px solid #293640;border-radius:12px;background:#090d11;overflow:hidden}.detail-head{padding:10px 12px;border-bottom:1px solid #222b32;background:#111820;display:flex;justify-content:space-between;align-items:center}.detail-head>div{min-width:0;display:flex;align-items:center;gap:9px}.detail-head>div>span:last-child{min-width:0;display:grid;gap:2px}.detail-head strong{color:#fff}.detail-head strong.pos{color:#22c55e}.detail-head strong.neg{color:#f87171}.detail-head small{color:#7f8c9b;font-size:10px}.detail-head button{width:28px}.trade-list{min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:8px;display:grid;align-content:start;gap:7px}.trade-card{border:1px solid #242424;border-radius:8px;background:#0f0f0f;color:#e5e7eb;padding:9px;display:grid;gap:7px}.top{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}.side,.money{min-width:0;display:grid;gap:3px}.trade-title{display:flex;align-items:center;gap:6px;min-width:0}.trade-title time{color:#64748b;font-size:10px;white-space:nowrap}.trade-provenance{color:#7dd3fc;font-size:9px;line-height:1.3}.trade-reason{color:#c4b5fd;font-size:9px;line-height:1.35}.side strong{font-size:12px;line-height:1;text-transform:uppercase}.side strong.buy{color:#86efac}.side strong.sell{color:#fca5a5}.side>span{color:#cbd5e1;font-size:13px;font-weight:800}.money{text-align:right;justify-items:end;font-variant-numeric:tabular-nums}.money>span:first-child{color:#f8fafc;font-size:14px;font-weight:900}.pnl{font-size:12px;font-weight:900}.trade-values{display:flex;align-items:center;justify-content:space-between;gap:8px;min-width:0}.trade-amount{color:#cbd5e1;font-size:13px;text-align:right}.trade-card.proposed{width:100%;text-align:left;cursor:pointer;animation:pending-border 1.8s ease-in-out infinite;border-color:#64748b}.trade-card.proposed:disabled{opacity:.65;cursor:wait;animation:none}.pending-sell{color:#94a3b8!important}@keyframes pending-border{50%{border-color:#cbd5e1;box-shadow:0 0 8px #94a3b844}}.pool,.trade-bottom{display:flex;gap:8px;flex-wrap:wrap;align-items:center;color:#8fa1b5;font-size:11px}.pool b{color:#cbd5e1}.trade-bottom{justify-content:space-between}.trade-bottom a{color:#93c5fd;text-decoration:none;font-weight:700}
 .detail-coin{display:flex;align-items:baseline;gap:5px;color:#f8fafc;font-size:11px}.detail-coin em{color:#7dd3fc;font-size:9px;font-style:normal}
 .exit-overlay{position:absolute;inset:0;z-index:5;display:grid;place-items:center;padding:14px;background:#000b}.exit-dialog{width:min(340px,100%);padding:16px;border:1px solid #3b4755;border-radius:14px;background:#101820;box-shadow:0 15px 45px #000a;color:#e2e8f0}.exit-dialog strong{font-size:14px}.exit-dialog p{font-size:12px;line-height:1.4;overflow-wrap:anywhere}.exit-actions{display:flex;justify-content:flex-end;gap:8px}.exit-actions button{padding:7px 12px;border:1px solid #48576a;border-radius:8px;background:#1a2531;color:#cbd5e1}.exit-actions button.confirm{border-color:#3b82f6;color:#fff} @media(max-width:440px){.position-head{padding-right:78px}.position-status{top:10px;right:10px}.position-card{grid-template-columns:46px minmax(0,1fr);gap:8px;padding:8px}.coin-art{width:46px;height:46px}.coin-links a{font-size:8px}}
</style>

